import { FORM_ERROR } from "final-form";
import { identify } from "./analytics";

function errorToFinalForm(err: any): Promise<any> {
  const msg =
    err?.body?.message ||
    err?.body?.error ||
    err?.message ||
    "Error desconocido, inténtalo de nuevo.";

  return Promise.resolve({ [FORM_ERROR]: msg });
}

/**
 * finalForm wraps a "naked" promise and turns the error into a
 * a format that FinalForm can understand.
 *
 * @param onSuccess the success callback to call when the API call was successful
 * @param p the not-yet-awaited promise to wrap in final form semantics
 */
export async function finalForm<T>(
  onSuccess: (result: T) => Promise<any>,
  p: Promise<any>,
): Promise<void> {
  return p.then(async (res) => {
    if (res.error) {
      return Promise.resolve({ [FORM_ERROR]: res.error });
    }

    await onSuccess(res.body);

    return Promise.resolve();
  }, errorToFinalForm);
}

export const apiRequestToOps = async (
  method: string,
  path: string,
  data: any,
) => {
  const resp = await makeRequest(method, path, data);

  // Any response coming from ops that has the top-level property posthogId
  // is interpreted to be a backend analytics id, so use it
  const posthogId = resp?.body?.posthogId;
  if (posthogId) {
    identify(posthogId);
  }

  return resp;
};

async function makeRequest(method: string, path: string, data: any) {
  const body = data ? JSON.stringify(data) : undefined;
  let fullPath = path;

  let headers = {
    "Content-Type": "application/json",
  };

  const res = await fetch(fullPath, { method, headers, body });
  if (res.status >= 400) {
    const errBody = await res.json();
    const msg = `cannot ${method} ${fullPath} (${res.status})`;
    const err: any = new Error(msg);
    err.body = errBody;
    err.status = res.status;
    throw err;
  }

  const respBody = await res.json();

  return { body: respBody };
}

export type WebleadPreviewResponse = {
  financial: {
    totalPrice: string; // outright purchase price for a financial lease 100% downpayment
    totalSavings: string;
    totalSavingsPercent: string;
  };
  operating: {
    annualEscalator: number;
    monthlyFee: string; // monthly fee for a 13 year 0% down subscription plan
    totalSavings: string;
    totalSavingsPercent: string;
  };
  numPanels: number;
  systemSizeWatts: number;
  annualKwhConsumption: number;
  annualKwhProduction: number;
  minAnnualKwhProduction: number;
  maxAnnualKwhProduction: number;
  noCostConsumptionIncreasePercentMin: string;
  noCostConsumptionIncreasePercentMax: string;
  customerT1Residential: boolean;
  t1ResidentialInfo: {
    dacAnnualKwhLimit: number;
    kwhUntilDac: string;
    currentCfeBillMxn: string;
    minimumCfeBillDacMxn: string;
    consumptionBreakdown: ConsumptionBreakdown[];
  };
};

type ConsumptionBreakdown = {
  cost: number;
  rate: number;
};

export async function webleadPreview(
  payload: any,
): Promise<{ body: WebleadPreviewResponse }> {
  let url: string;
  if (process.env.NODE_ENV === "production") {
    url = "https://goapi.thinkbright.mx/proposals/weblead";
  } else {
    url = "http://localhost:8090/proposals/weblead";
  }

  const { body } = await apiRequestToOps("POST", url, payload);
  return {
    body: body as WebleadPreviewResponse,
  };
}

export type proposalSummaryResponse = {
  leadName: string;
  monthlyFee: string;
  solarRatio: string;
  systemSize: string;
  ec: {
    name: string | null;
    phone: string | null;
    email: string | null;
  };
};

export async function proposalSummary(
  uuid: string,
): Promise<{ body: proposalSummaryResponse }> {
  let url: string;
  if (process.env.NODE_ENV === "production") {
    url = `https://api.thinkbright.mx/leads/${uuid}/proposalSummary`;
  } else {
    url = `http://localhost:8089/leads/${uuid}/proposalSummary`;
  }

  const { body } = await apiRequestToOps("GET", url, null);
  return {
    body: body as proposalSummaryResponse,
  };
}
